import { useState, useEffect } from 'react';
import { CreatePersonWithRelationData } from '@/hooks/usePersonProfiles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trash2, Info } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNativeConfirmation } from '@/hooks/useNativeConfirmation';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { LinkedAccountBadge } from './LinkedAccountBadge';
import { useProfileOwnership } from '@/hooks/useProfileOwnership';

interface PersonFormContentProps {
  onSubmit: (data: CreatePersonWithRelationData) => Promise<void>;
  initialData?: Partial<CreatePersonWithRelationData> & { id?: string };
  isSubmitting?: boolean;
  onCancel?: () => void;
  showActions?: boolean;
  onFormDataChange?: (data: CreatePersonWithRelationData) => void;
  onDelete?: (personId: string) => Promise<void>;
}

export const PersonFormContent = ({ 
  onSubmit, 
  initialData,
  isSubmitting = false,
  onCancel,
  showActions = true,
  onFormDataChange,
  onDelete
}: PersonFormContentProps) => {
  const isMobile = useIsMobile();
  const { showConfirmation } = useNativeConfirmation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { isProfileOwner } = useProfileOwnership();
  const [formData, setFormData] = useState<CreatePersonWithRelationData>({
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    category: initialData?.category || 'family',
    date_of_birth: initialData?.date_of_birth || '',
    gender: initialData?.gender,
    phone_primary: initialData?.phone_primary || '',
    email: initialData?.email || '',
    address: initialData?.address || '',
    notes: initialData?.notes || '',
    emergency_contact: initialData?.emergency_contact || false,
    medical_speciality: initialData?.medical_speciality || '',
    relationship_type: initialData?.relationship_type || '',
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  
  const isEditing = !!(initialData?.id);
  
  // Détection du compte lié
  const hasLinkedAccount = !!(initialData as any)?.profile_id;
  const isAccountOwner = hasLinkedAccount && isProfileOwner((initialData as any)?.profile_id);
  
  // Champs protégés pour les comptes liés
  const isFieldProtected = hasLinkedAccount && !isAccountOwner;

  const updateFormData = (newData: Partial<CreatePersonWithRelationData>) => {
    const updatedData = { ...formData, ...newData };
    setFormData(updatedData);
    onFormDataChange?.(updatedData);
  };

  // Update formData when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        category: initialData.category || 'family',
        date_of_birth: initialData.date_of_birth || '',
        gender: initialData.gender,
        phone_primary: initialData.phone_primary || '',
        email: initialData.email || '',
        address: initialData.address || '',
        notes: initialData.notes || '',
        emergency_contact: initialData.emergency_contact || false,
        family_role: initialData.family_role,
        medical_speciality: initialData.medical_speciality || '',
        relationship_type: initialData.relationship_type || '',
      });
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name.trim()) return;

    try {
      // Clean up empty date fields before submission
      const cleanedData = {
        ...formData,
        date_of_birth: formData.date_of_birth || undefined,
      };
      await onSubmit(cleanedData);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleDelete = async () => {
    if (!initialData?.id || !onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(initialData.id);
      toast({
        title: "Personne supprimée",
        description: "La personne a été supprimée avec succès.",
      });
    } catch (error) {
      console.error('❌ Error deleting person:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la personne.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = async () => {
    // Use native confirmation on mobile Capacitor app
    const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor;
    
    if (isMobile && isCapacitor) {
      const confirmed = await showConfirmation({
        title: "Supprimer cette personne ?",
        message: "Cette action est irréversible. La personne sera définitivement supprimée de vos contacts.",
        okButtonTitle: "Supprimer",
        cancelButtonTitle: "Annuler"
      });
      
      if (confirmed) {
        handleDelete();
      }
    } else {
      // On web, show the AlertDialog
      setShowDeleteConfirmDialog(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Badge et message informatif pour comptes liés */}
      {hasLinkedAccount && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">
              {initialData?.first_name} {initialData?.last_name}
            </h3>
            <LinkedAccountBadge isOwner={isAccountOwner} />
          </div>
          
          {!isAccountOwner && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Cette personne a un compte Clan. Seule elle peut modifier ses informations personnelles (nom, prénom, email, avatar, date de naissance).
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      <form id="person-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Hidden button to trigger delete from modal footer */}
        <button
          type="button"
          data-delete-person-button
          onClick={handleDeleteClick}
          className="hidden"
        />
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">Prénom *</Label>
            <Input
              id="first_name"
              value={formData.first_name}
              onChange={(e) => updateFormData({ first_name: e.target.value })}
              disabled={isFieldProtected}
              required
              className={isFieldProtected ? "bg-muted" : ""}
            />
            {isFieldProtected && (
              <p className="text-xs text-muted-foreground mt-1">
                Seul le propriétaire peut modifier ce champ
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="last_name">Nom *</Label>
            <Input
              id="last_name"
              value={formData.last_name}
              onChange={(e) => updateFormData({ last_name: e.target.value })}
              disabled={isFieldProtected}
              required
              className={isFieldProtected ? "bg-muted" : ""}
            />
            {isFieldProtected && (
              <p className="text-xs text-muted-foreground mt-1">
                Seul le propriétaire peut modifier ce champ
              </p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="category">Catégorie</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              type="button"
              variant={formData.category === 'family' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'family' })}
              className="h-8 px-3"
            >
              👨‍👩‍👧‍👦 Famille
            </Button>
            <Button
              type="button"
              variant={formData.category === 'extended_family' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'extended_family' })}
              className="h-8 px-3"
            >
              👪 Famille étendue
            </Button>
            <Button
              type="button"
              variant={formData.category === 'friends' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'friends' })}
              className="h-8 px-3"
            >
              👥 Amis
            </Button>
            <Button
              type="button"
              variant={formData.category === 'school' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'school' })}
              className="h-8 px-3"
            >
              🎓 École
            </Button>
            <Button
              type="button"
              variant={formData.category === 'sport' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'sport' })}
              className="h-8 px-3"
            >
              ⚽ Sport
            </Button>
            <Button
              type="button"
              variant={formData.category === 'services' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'services' })}
              className="h-8 px-3"
            >
              🛠️ Services
            </Button>
            <Button
              type="button"
              variant={formData.category === 'medical_other' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'medical_other' })}
              className="h-8 px-3"
            >
              ⚕️ Médical
            </Button>
            <Button
              type="button"
              variant={formData.category === 'professional' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFormData({ category: 'professional' })}
              className="h-8 px-3"
            >
              💼 Professionnel
            </Button>
          </div>
        </div>

        {/* Family Role - only show for family category */}
        {formData.category === 'family' && (
          <div>
            <Label htmlFor="family_role">Rôle dans la famille</Label>
            <Select value={formData.family_role || ''} onValueChange={(value: any) => updateFormData({ family_role: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un rôle" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="father">Papa</SelectItem>
                <SelectItem value="mother">Maman</SelectItem>
                <SelectItem value="son">Fils</SelectItem>
                <SelectItem value="daughter">Fille</SelectItem>
                <SelectItem value="grandfather">Grand-père</SelectItem>
                <SelectItem value="grandmother">Grand-mère</SelectItem>
                <SelectItem value="guardian_male">Tuteur</SelectItem>
                <SelectItem value="guardian_female">Tutrice</SelectItem>
                <SelectItem value="pet">Animal de compagnie</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Extended Family Role - using relationship_type field with dropdown */}
        {formData.category === 'extended_family' && (
          <div>
            <Label htmlFor="relationship_type">Relation familiale</Label>
            <Select value={formData.relationship_type || ''} onValueChange={(value: any) => updateFormData({ relationship_type: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une relation" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="grandfather">Grand-père</SelectItem>
                <SelectItem value="grandmother">Grand-mère</SelectItem>
                <SelectItem value="uncle">Oncle</SelectItem>
                <SelectItem value="aunt">Tante</SelectItem>
                <SelectItem value="brother">Frère</SelectItem>
                <SelectItem value="sister">Sœur</SelectItem>
                <SelectItem value="male_cousin">Cousin</SelectItem>
                <SelectItem value="female_cousin">Cousine</SelectItem>
                <SelectItem value="nephew">Neveu</SelectItem>
                <SelectItem value="niece">Nièce</SelectItem>
                <SelectItem value="stepfather">Beau-père</SelectItem>
                <SelectItem value="stepmother">Belle-mère</SelectItem>
                <SelectItem value="brother_in_law">Beau-frère</SelectItem>
                <SelectItem value="sister_in_law">Belle-sœur</SelectItem>
                <SelectItem value="pet">Animal de compagnie</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Medical Speciality - only show for medical_other category */}
        {formData.category === 'medical_other' && (
          <div>
            <Label htmlFor="medical_speciality">Spécialité médicale</Label>
            <Select value={formData.medical_speciality || ''} onValueChange={(value: any) => updateFormData({ medical_speciality: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une spécialité" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="medecin_generaliste">Médecin généraliste</SelectItem>
                <SelectItem value="pediatre">Pédiatre</SelectItem>
                <SelectItem value="dentiste">Dentiste</SelectItem>
                <SelectItem value="ophtalmologue">Ophtalmologue</SelectItem>
                <SelectItem value="cardiologue">Cardiologue</SelectItem>
                <SelectItem value="dermatologue">Dermatologue</SelectItem>
                <SelectItem value="pharmacien">Pharmacien</SelectItem>
                <SelectItem value="psychologue">Psychologue</SelectItem>
                <SelectItem value="kinesitherapeute">Kinésithérapeute</SelectItem>
                <SelectItem value="veterinaire">Vétérinaire</SelectItem>
                <SelectItem value="autre_medical">Autre professionnel médical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Relationship Type - only show for friends category */}
        {formData.category === 'friends' && (
          <div>
            <Label htmlFor="relationship_type">Type de relation</Label>
            <Select value={formData.relationship_type || ''} onValueChange={(value: any) => updateFormData({ relationship_type: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner le type" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="ami_proche">Ami proche</SelectItem>
                <SelectItem value="voisin">Voisin</SelectItem>
                <SelectItem value="autre_ami">Autre ami</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* School specific fields */}
        {formData.category === 'school' && (
          <div>
            <Label htmlFor="relationship_type">Fonction</Label>
            <Select value={formData.relationship_type || ''} onValueChange={(value: any) => updateFormData({ relationship_type: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une fonction" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="professeur">Professeur</SelectItem>
                <SelectItem value="instituteur">Instituteur</SelectItem>
                <SelectItem value="directeur">Directeur</SelectItem>
                <SelectItem value="surveillant">Surveillant</SelectItem>
                <SelectItem value="autre_ecole">Autre personnel</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Sport specific fields */}
        {formData.category === 'sport' && (
          <div>
            <Label htmlFor="relationship_type">Fonction</Label>
            <Select value={formData.relationship_type || ''} onValueChange={(value: any) => updateFormData({ relationship_type: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une fonction" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="entraineur">Entraîneur</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="prof_sport">Professeur de sport</SelectItem>
                <SelectItem value="autre_sport">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Services specific fields */}
        {formData.category === 'services' && (
          <div>
            <Label htmlFor="relationship_type">Type de service</Label>
            <Select value={formData.relationship_type || ''} onValueChange={(value: any) => updateFormData({ relationship_type: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un type" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="baby_sitter">Baby-sitter</SelectItem>
                <SelectItem value="nounou">Nounou</SelectItem>
                <SelectItem value="aide_menagere">Aide ménagère</SelectItem>
                <SelectItem value="jardinier">Jardinier</SelectItem>
                <SelectItem value="autre_service">Autre service</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Professional specific fields */}
        {formData.category === 'professional' && (
          <div>
            <Label htmlFor="relationship_type">Type de relation</Label>
            <Select value={formData.relationship_type || ''} onValueChange={(value: any) => updateFormData({ relationship_type: value || undefined })}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un type" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="collegue">Collègue</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="fournisseur">Fournisseur</SelectItem>
                <SelectItem value="autre_pro">Autre relation professionnelle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="date_of_birth">Date de naissance</Label>
            <Input
              id="date_of_birth"
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => updateFormData({ date_of_birth: e.target.value })}
              disabled={isFieldProtected}
              className={`text-sm ${isFieldProtected ? "bg-muted" : ""}`}
            />
            {isFieldProtected && (
              <p className="text-xs text-muted-foreground mt-1">
                Seul le propriétaire peut modifier ce champ
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="gender">Genre</Label>
            <Select value={formData.gender || ''} onValueChange={(value: any) => updateFormData({ gender: value || undefined })}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="male">Homme</SelectItem>
                <SelectItem value="female">Femme</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
                <SelectItem value="prefer_not_to_say">Préfère ne pas dire</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="phone_primary">Téléphone</Label>
          <Input
            id="phone_primary"
            type="tel"
            value={formData.phone_primary}
            onChange={(e) => updateFormData({ phone_primary: e.target.value })}
            placeholder="+33 6 12 34 56 78"
          />
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData({ email: e.target.value })}
            disabled={isFieldProtected}
            className={isFieldProtected ? "bg-muted" : ""}
          />
          {isFieldProtected && (
            <p className="text-xs text-muted-foreground mt-1">
              Seul le propriétaire peut modifier ce champ
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="address">Adresse</Label>
          <Input
            id="address"
            value={formData.address}
            onChange={(e) => updateFormData({ address: e.target.value })}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="emergency_contact"
            checked={formData.emergency_contact}
            onCheckedChange={(checked) => updateFormData({ emergency_contact: checked })}
          />
          <Label htmlFor="emergency_contact">Contact d'urgence</Label>
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => updateFormData({ notes: e.target.value })}
            rows={3}
          />
        </div>


        {showActions && (
          <div className="flex gap-4 pt-6">
            <Button 
              type="submit"
              disabled={isSubmitting || !formData.first_name.trim()}
              className="flex-1"
              variant="persons"
            >
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Annuler
              </Button>
            )}
          </div>
        )}
      </form>

      {/* Dialog pour confirmer suppression */}
      <AlertDialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette personne ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La personne sera définitivement supprimée de vos contacts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteConfirmDialog(false)}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                setShowDeleteConfirmDialog(false);
                await handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};